/**
 * Demo Preview — floating chip + exit affordance.
 *
 * Rendered globally (App.js) so it's visible on every page once a demo
 * session is active. Hidden otherwise. Shows the impersonated identity
 * + countdown to expiry; the "Exit" button restores the admin's real
 * session and navigates back to /admin.
 */

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDemoPreview } from "@/contexts/DemoPreviewContext";
import { useLang } from "@/i18n";
import { Eye, LogOut } from "lucide-react";

const fmtMmSs = (totalSeconds) => {
  const s = Math.max(0, Math.floor(totalSeconds));
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

const DemoPreviewBadge = () => {
  const { t } = useLang();
  const navigate = useNavigate();
  const { isActive, identity, session, endSession } = useDemoPreview();
  const [remaining, setRemaining] = useState(0);

  useEffect(() => {
    if (!session?.expires_at) return;
    const tick = () => {
      const diff = (new Date(session.expires_at).getTime() - Date.now()) / 1000;
      setRemaining(diff);
      if (diff <= 0) onExit();
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.expires_at]);

  if (!isActive || !identity) return null;

  const onExit = async () => {
    await endSession();
    navigate("/admin");
    window.location.reload();
  };

  const roleLabel = t(`demoPreview.roles.${identity.profile_type}`) || identity.profile_type;

  return (
    <div
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[60] flex items-center gap-2 rounded-full bg-purple-600 text-white shadow-lg ps-3 pe-2 py-1.5 text-[12px] font-semibold border border-purple-400"
      data-testid="demo-preview-badge"
      role="status"
      aria-live="polite"
    >
      <Eye className="w-3.5 h-3.5" />
      <span className="uppercase tracking-wide">{t("demoPreview.chip.badge")}</span>
      <span className="text-purple-200">·</span>
      <span className="truncate max-w-[180px]" data-testid="demo-preview-badge-identity">
        {identity.name}
      </span>
      <span className="text-purple-200">·</span>
      <span className="text-purple-100 text-[11px]" data-testid="demo-preview-badge-role">
        {roleLabel}
      </span>
      <span className="text-purple-200">·</span>
      <span className="font-mono tabular-nums text-[11px]" data-testid="demo-preview-badge-timer">
        {t("demoPreview.chip.expiresIn")} {fmtMmSs(remaining)}
      </span>
      <button
        type="button"
        onClick={onExit}
        className="ms-1 inline-flex items-center gap-1 rounded-full bg-white/15 hover:bg-white/25 px-2 py-0.5 transition"
        data-testid="demo-preview-badge-exit"
      >
        <LogOut className="w-3 h-3" />
        {t("demoPreview.chip.exit")}
      </button>
    </div>
  );
};

export default DemoPreviewBadge;
